import React, { useEffect, useMemo, useState } from "react";
import { Search, ChevronLeft, ChevronRight, ArrowUpDown } from "lucide-react";
import { EmptyState } from "./VB";

const PAGE_SIZE = 50;

// Datatable mutasi rekening: pencarian, filter arah, urut tanggal, paginasi 50 per halaman.
// Dipakai Arus Kas → Mutasi (admin) dan menu Tabungan (Kepala Group).
// mode "admin" menampilkan jenis asli + referensi; mode "client" menampilkan Kredit/Debit saja.
export default function MutasiTable({ items, fmt, mode = "admin" }) {
  const [q, setQ] = useState("");
  const [arah, setArah] = useState("");
  const [desc, setDesc] = useState(true);
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    let out = items;
    if (arah) out = out.filter((m) => m.arah === arah);
    if (s) {
      out = out.filter((m) =>
        (m.deskripsi || "").toLowerCase().includes(s) ||
        (m.tanggal || "").includes(s) ||
        (m.jenis || "").toLowerCase().includes(s)
      );
    }
    // items datang terbaru-dulu (created_at desc); balik urutan kalau minta terlama-dulu
    return desc ? out : [...out].reverse();
  }, [items, q, arah, desc]);

  useEffect(() => { setPage(1); }, [q, arah, desc, items]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * PAGE_SIZE;
  const rows = filtered.slice(start, start + PAGE_SIZE);
  const label = (m) => (mode === "client" ? (m.arah === "Masuk" ? "Kredit" : "Debit") : m.jenis);

  const pager = (
    <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, flexWrap: "wrap" }}>
      <span style={{ color: "#666" }}>
        {filtered.length === 0 ? "0 data" : `Menampilkan ${start + 1}–${Math.min(start + PAGE_SIZE, filtered.length)} dari ${filtered.length} data`}
      </span>
      <button className="vb-btn" style={{ padding: "2px 6px" }} disabled={safePage <= 1} onClick={() => setPage(1)}>«</button>
      <button className="vb-btn" style={{ padding: "2px 6px" }} disabled={safePage <= 1} onClick={() => setPage(safePage - 1)}><ChevronLeft className="w-3 h-3" /></button>
      <span>Hal.
        <select className="vb-select" style={{ width: 70, marginLeft: 4 }} value={safePage} onChange={(e) => setPage(Number(e.target.value))}>
          {Array.from({ length: totalPages }, (_, i) => <option key={i + 1} value={i + 1}>{i + 1}</option>)}
        </select>
        {" "}/ {totalPages}
      </span>
      <button className="vb-btn" style={{ padding: "2px 6px" }} disabled={safePage >= totalPages} onClick={() => setPage(safePage + 1)}><ChevronRight className="w-3 h-3" /></button>
      <button className="vb-btn" style={{ padding: "2px 6px" }} disabled={safePage >= totalPages} onClick={() => setPage(totalPages)}>»</button>
    </div>
  );

  return (
    <div>
      <div style={{ display: "flex", gap: 6, marginBottom: 8, flexWrap: "wrap", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <div style={{ position: "relative" }}>
            <Search className="w-3 h-3" style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", color: "#888" }} />
            <input className="vb-input" style={{ width: 240, paddingLeft: 24 }} placeholder="Cari keterangan / tanggal..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="mutasi-search" />
          </div>
          <select className="vb-select" style={{ width: 150 }} value={arah} onChange={(e) => setArah(e.target.value)} data-testid="mutasi-arah">
            <option value="">Semua</option>
            <option value="Masuk">{mode === "client" ? "Kredit" : "Masuk"}</option>
            <option value="Keluar">{mode === "client" ? "Debit" : "Keluar"}</option>
          </select>
        </div>
        {pager}
      </div>

      {rows.length === 0 ? <EmptyState message={items.length ? "Tidak ada mutasi yang cocok" : "Belum ada mutasi"} /> : (
        <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
          <table className="vb-table" data-testid="mutasi-table">
            <thead>
              <tr>
                <th style={{ width: 40, textAlign: "center" }}>#</th>
                <th style={{ cursor: "pointer", whiteSpace: "nowrap" }} onClick={() => setDesc((d) => !d)} title="Urutkan">
                  Tanggal <ArrowUpDown className="w-3 h-3 inline" /> <span style={{ fontSize: 10, color: "#666" }}>{desc ? "terbaru" : "terlama"}</span>
                </th>
                <th>Jenis</th>
                <th>Keterangan</th>
                {mode === "admin" && <th>Referensi</th>}
                <th style={{ textAlign: "right" }}>Nominal</th>
                <th style={{ textAlign: "right" }}>Saldo Setelah</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((m, i) => {
                const masuk = m.arah === "Masuk";
                return (
                  <tr key={m.id}>
                    <td style={{ textAlign: "center", color: "#666", fontSize: 11 }}>{start + i + 1}</td>
                    <td style={{ fontSize: 11, whiteSpace: "nowrap" }}>{m.tanggal}</td>
                    <td><span className={`vb-badge ${masuk ? "vb-badge-approved" : "vb-badge-rejected"}`}>{label(m)}</span></td>
                    <td>{m.deskripsi}</td>
                    {mode === "admin" && <td style={{ fontSize: 10, color: "#666" }}>{m.reference_type !== "manual" ? m.reference_type : "-"}</td>}
                    <td style={{ textAlign: "right", color: masuk ? "#4f8a10" : "#d8000c", fontWeight: 600, whiteSpace: "nowrap" }}>
                      {masuk ? "+ " : "- "}{fmt(m.nominal)}
                    </td>
                    <td style={{ textAlign: "right", fontWeight: 600, whiteSpace: "nowrap", color: m.saldo_setelah < 0 ? "#d8000c" : undefined }}>{fmt(m.saldo_setelah)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {totalPages > 1 && <div style={{ marginTop: 8, display: "flex", justifyContent: "flex-end" }}>{pager}</div>}
    </div>
  );
}
